import React, { useCallback, useMemo, useState } from 'react';
import clsx from 'clsx';
import { FormattedMessage, useIntl } from 'react-intl';
import Search, { SearchEvent } from 'carbon-react/lib/components/search';
import Button from 'carbon-react/lib/components/button';

import { UIDualListPanel } from '@futrli/ui-components';

import { AvailableAccountsList } from './available-accounts-list/available-accounts-list';
import { OffsetEliminatedGroupsList } from './offset-eliminated-groups-list/offset-eliminated-groups-list';
import { OffsetEliminationGroup } from '../../../core/api/consolidation';
import { useConsolidationContext } from '../../consolidation-context/consolidation-context';
import styles from './eliminations.scss';
import { OffsetEliminationsGroupSidebar } from './elimination-modals/offset-eliminations-group-sidebar';

export interface EliminationsProps {
  editing?: boolean;
}

export const Eliminations = ({ editing }: EliminationsProps) => {
  const intl = useIntl();

  const [searchCriteria, setSearchCriteria] = useState('');

  const {
    eliminations: {
      eliminationGroups,
      handleAddEliminationGroupAccount,
      handleCreateEliminationGroup,
      handleMoveEliminationGroupAccount,
      handleRemoveEliminationGroupAccount
    }
  } = useConsolidationContext();

  const [currentDragItem, setCurrentDragItem] = useState({
    id: '',
    isAlreadySelected: false
  });

  const alreadySelectedNodeIds = useMemo(
    () =>
      Object.values(eliminationGroups)
        .flat()
        .map(g => g.accounts)
        .flat(),
    [eliminationGroups]
  );

  const handleDragEnd = useCallback(() => {
    setCurrentDragItem({
      id: '',
      isAlreadySelected: false
    });
  }, []);

  const handleDragStart = useCallback(
    (_event: React.DragEvent, id: string) => {
      setCurrentDragItem({
        id,
        isAlreadySelected: alreadySelectedNodeIds.includes(id)
      });
    },
    [alreadySelectedNodeIds]
  );

  const handleDropOnEliminationGroup = useCallback(
    (event: React.DragEvent, group: OffsetEliminationGroup) => {
      event.preventDefault();
      if (currentDragItem.isAlreadySelected) {
        handleMoveEliminationGroupAccount(group, currentDragItem.id);
      } else {
        handleAddEliminationGroupAccount(group, currentDragItem.id);
      }
      handleDragEnd();
    },
    [
      currentDragItem.id,
      currentDragItem.isAlreadySelected,
      handleAddEliminationGroupAccount,
      handleDragEnd,
      handleMoveEliminationGroupAccount
    ]
  );

  const handleDropOnCOA = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();
      handleRemoveEliminationGroupAccount(currentDragItem.id);
      handleDragEnd();
    },
    [currentDragItem.id, handleDragEnd, handleRemoveEliminationGroupAccount]
  );

  const handleSearchChange = useCallback((event: SearchEvent) => {
    setSearchCriteria(event.target.value);
  }, []);

  return (
    <div onDragEnd={handleDragEnd}>
      <UIDualListPanel
        data-testid="EliminationsPanel"
        className={clsx(styles.container, { [styles.editing]: editing })}
        search1={
          <Search
            label={intl.formatMessage({
              id: 'common.search_ellipsis',
              defaultMessage: 'Search...'
            })}
            value={searchCriteria}
            onChange={handleSearchChange}
          />
        }
        list1={
          <AvailableAccountsList
            allowDrop={currentDragItem.isAlreadySelected}
            isDragging={!!currentDragItem.id}
            onDragStart={handleDragStart}
            onDrop={handleDropOnCOA}
            searchCriteria={searchCriteria}
          />
        }
        title1={<FormattedMessage id="consolidation.available_accounts" defaultMessage="Available accounts" />}
        search2={
          <div className={styles.buttonContainer}>
            <Button
              className={styles.createButton}
              onClick={handleCreateEliminationGroup}
              buttonType="primary"
              size="small"
            >
              <FormattedMessage id="common.create_new_group.button" defaultMessage="Create new group" />
            </Button>
          </div>
        }
        title2={<FormattedMessage id="consolidation.elimination_groups" defaultMessage="Elimination groups" />}
        list2={
          <OffsetEliminatedGroupsList
            dragItemId={currentDragItem.id}
            isDragging={!!currentDragItem.id}
            onDragStart={handleDragStart}
            onDrop={handleDropOnEliminationGroup}
          />
        }
      />
      <OffsetEliminationsGroupSidebar />
    </div>
  );
};
